/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import SystemService from '../../service/system';
import moment from 'moment';
import WyUtils from '../../library/wy-utils.js';

const state = {
  tableModel: createTableModelObj(),
  localStorage: {},
  //"devices": ["mobile","desktop","blackberry","PDA"],
  channelOriginList: ['webchat', 'sms', 'whatsapp', 'abc', 'facebook'],
  languageOriginList: ['english', 'french', 'spanish'],
  legendColors: [],
  legendDefaultColors: ["#4B67A3", "#5FBBAA", "#8E88DC", "#E5996D", "#F8E4A2", "#D65858", "#86D3FE", "#AE69CE", "#F9BA7F", "#598EAC", "#67B157", "#0F3D75", "#DADE46", "#657E55", "#EF8D8D"],
  legendOriginColors: [],
  deviceOriginList: ['desktop', 'mobile', 'tablet', '--'],
  processList: [],
  updatedProcessList: [],
  processNewCounter: null,
  alertList: [],
  alertNewCounter: null,
  processAlertStatus: 0, 
  defaultTarget: {},
  contactReasonsTargets: []
}

const getters = {
  localStorage: state => {    
    return state.tableModel ? state.localStorage : {}
  },

  tableModel: state => {    
    return state.tableModel ? state.tableModel : {}
  },

  processList: state => {    
    return state.processList
  },

  updatedProcessList: state => {    
    return state.updatedProcessList
  },

  processNewCounter: state => {    
    return state.processNewCounter
  },

  alertList: state => {    
    return state.alertList
  },

  alertNewCounter: state => {    
    return state.alertNewCounter
  },

  processAlertStatus: state => {
    return state.processAlertStatus
  },

  numOfTableRowsSelected: state => {
    return state.tableModel.selectedRows ? state.tableModel.selectedRows.length : 0
  },

  legendColors: state => state.legendColors,
  legendDefaultColors: state => state.legendDefaultColors,
  legendOriginColors: state => state.legendOriginColors,
  channelOriginList: state => state.channelOriginList,
  languageOriginList: state => state.languageOriginList,
  deviceOriginList: state => state.deviceOriginList,
  defaultTarget: state => state.defaultTarget,
  contactReasonsTargets: state => state.contactReasonsTargets
}

const mutations = {
  // ******************** table model ********************
  updateTableModelResult(state, {results, totalRecs, totalPages, totalNumber}) {
    if(state.tableModel) {
      state.tableModel.results = results;
      state.tableModel.totalRecs = totalRecs;
      state.tableModel.totalPages = totalPages;
      state.tableModel.totalNumber = totalNumber;
    }
  },

  updateTableModelSelectedRows(state, rows) {
    if(state.tableModel && rows) {
      state.tableModel.selectedRows = rows.filter(row => (row.isEditable===undefined || row.isEditable===true));
    }
  },

  ressetTableModel(state) {
    if(state.tableModel) {
      state.tableModel = createTableModelObj()
    }
  },

  updateTableModelResultWithDynamicColumns(state, {results, sections, totalRecs, totalPages, totalNumber}) {
    if(state.tableModel) {
      state.tableModel.results = results;
      state.tableModel.sections = sections;
      state.tableModel.totalRecs = totalRecs;
      state.tableModel.totalPages = totalPages;
      state.tableModel.totalNumber = totalNumber;
    }
  },

  updateLocalStorage(state, {key, data}) {
    state.localStorage[key] = data;
  },

  // ******************** update legend colors ********************
  updateLegendColors(state, {colors}) {
    state.legendColors = colors
  },
  updateLegendOriginColors(state, {colors}) {
    state.legendOriginColors = colors
  },

  updateProcessList(state, {result, options, counter}) {
    options = options || {};

    if(result.length > 0) {
      state.processNewCounter = counter;
      
      // get previous procssing actions
      if(options.showAlert && state.processList.length > 0) {
        const updatedProcessList = [];
        for(const newProcess of result.filter(el => el.processStatus!= "pending")) {
          let process = state.processList.find(process => process.processId==newProcess.processId);
          if(!process || process.processStatus != newProcess.processStatus) {
            updatedProcessList.push(newProcess);
          }
        }
        state.updatedProcessList = updatedProcessList;
      }
    }

    state.processList = result;
  },

  updateAlertList(state, {result, counter}) {
    state.alertNewCounter = counter;
    state.alertList = result;
  },

  updateProcessAlertStatus(state, status) {
    state.processAlertStatus = status;
  },

  resetProcessNewItems(state) {
    state.processNewCounter = 0;
  },

  resetAlertNewItems(state) {
    state.alertNewCounter = 0;
  },

  updateDefaultTarget(state, {defaultTarget}) {
    state.defaultTarget = defaultTarget
  },
  updateContactReasonsTargets(state, {targets}) {
    state.contactReasonsTargets = targets
  }
}

const actions = {
  // eslint-disable-next-line no-unused-vars
  async initializeServerData(_) {

  },

  async updateLocalStorage({ commit }, {key, data}) {
    commit('updateLocalStorage', {key: key, data: data});
  },

  async fetchMockApiList({getters}) {
    let res = await SystemService.fetchMockApiList(getters.authUser);
    if(res.status == 200 && res.data) {
      return res.data;
    }
    return null;
  },

  async fetchMockApiData({getters}, apiPath) {
    let res = await SystemService.fetchMockApiData(getters.authUser, apiPath);
    if(res.status == 200 && res.data) {
      return res.data;
    }
    return null;
  },

  async updateMockApiData({getters}, data) {
    return SystemService.updateMockApiData(getters.authUser, data);
  },

  async fetchProcessList({getters, commit}, options) {
    const maxRecords = getters.authUser.config.app.configs.maxProcessList || 100;
    let res = await SystemService.fetchProcessList(getters.authUser, {max: maxRecords});
    if(res.status == 200 && res.data) {
      // process notifications
      const processStatus = res.data.processStatus;
      if(processStatus.length > 0) {
        let processNewCounter = 0;
        let processLastActionDate = WyUtils.getBrowserLocalStorageByUser(getters.authUser, "processLastActionDate");
        if(processLastActionDate && processLastActionDate != "undefined") {
          const lastDate = moment(processLastActionDate);
          processNewCounter = processStatus.filter(item => {item.actionDate = item.actionDate || item.startDate; item.isNew = moment(item.actionDate).isAfter(lastDate); return item.isNew}).length;
        }
        else {
          processNewCounter = 0;
          WyUtils.updateBrowserLocalStorageByUser(getters.authUser, "processLastActionDate", processStatus[0].actionDate);
        }
        commit("updateProcessList", {result: processStatus, options: options, counter: processNewCounter});
      }
      else {
        commit("updateProcessList", {result: [], options: options, counter: 0});
      }

      // alert notifications
      const alertStatus = res.data.processAlert || [];
      alertStatus.map((item) => {
        item.alerts.sort((a,b) => (a.label.toLowerCase() > b.label.toLowerCase()) ? 1 : ((b.label.toLowerCase() > a.label.toLowerCase()) ? -1 : 0));
      })

      if(alertStatus.length > 0) {
        let alertNewCounter = 0;
        let alertLastActionDate = WyUtils.getBrowserLocalStorageByUser(getters.authUser, "alertLastActionDate");
        if(alertLastActionDate && alertLastActionDate != "undefined") {        
          const lastDate = moment(alertLastActionDate);
          alertNewCounter = alertStatus.filter(item => {item.isNew = moment(item.actionDate).isAfter(lastDate); return item.isNew}).length;
        }
        else {
          const lastDate = moment(alertLastActionDate);
          alertNewCounter = alertStatus.filter(item => {item.isNew = moment(item.actionDate).isAfter(lastDate); return item.isNew}).length;
          // WyUtils.updateBrowserLocalStorageByUser(getters.authUser, "alertLastActionDate", alertStatus[0].actionDate);
        }
        commit("updateAlertList", {result: alertStatus, options: options, counter: alertNewCounter});
      }
      else {
        commit("updateAlertList", {result: [], options: options, counter: 0});
      }

      // update rules setup status
      const processAlertStatus = res.data.processAlertStatus;
      commit("updateProcessAlertStatus", processAlertStatus);
    }
  },

  async resetProcessNewItems({commit, state, getters}) {
    if(state.processList.length > 0) {
      WyUtils.updateBrowserLocalStorageByUser(getters.authUser, "processLastActionDate", state.processList[0].actionDate);
    }
    commit("resetProcessNewItems");
  },

  async resetAlertNewItems({commit, state, getters}) {
    if(state.alertList.length > 0) {
      WyUtils.updateBrowserLocalStorageByUser(getters.authUser, "alertLastActionDate", state.alertList[0].actionDate);
    }
    commit("resetAlertNewItems");
  },

  async fetchFileContentUrl({getters}, data) {
    let res = await SystemService.fetchFileContent(getters.authUser, data);
    if(res.status == 200 && res.body) {
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    }
    else {
      return null;
    }
  },

  async postIFrameEvent(_context, data) {
    if(window.parent) {
      window.parent.postMessage(data, '*');
      if(window.parent.opener) {
        window.parent.opener.postMessage(data, '*');
      }
    }
  }
}

function createTableModelObj() {
  return {    
    selectedRows: null,
    results: [],
    totalRecs: 0,
    totalPages: 0,
    sorting: null
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}